import { SectionHeadingContainer } from '@/components/SectionHeadingContainer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const businesses = [
  {
    icon: 'ri-line-chart-line',
    title: '営業代行事業',
    desc: 'クライアント企業の営業活動を担い、対面・電話を通じたセールス支援を行います。単なる代行ではなく、成果にコミットした実践型の営業支援を提供しています。',
  },
  {
    icon: 'ri-lightbulb-line',
    title: 'コンサルティング事業',
    desc: '組織の課題を可視化し、人材育成と営業戦略の両面から改善を行います。現場視点と実務経験をもとに、実行できる提案を行います。',
  },
  {
    icon: 'ri-global-line',
    title: '広告マーケティング事業',
    desc: '求人媒体やデジタル広告を活用し、集客および採用活動の支援を行います。運用・改善を通じて、継続的に成果を生み出す仕組みを構築します。',
  },
];

/** index.css の .fade-in-up.delay-* に合わせた段階表示 */
const itemDelays = ['delay-3', 'delay-3', 'delay-4'] as const;

export default function BusinessSection() {
  const ref = useScrollAnimation();

  return (
    <section
      id="business"
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: '#F5F2EB', paddingTop: '65px', paddingBottom: '130px' }}
      aria-label="事業内容"
    >
      {/* Message / Company と同じ左端の縦ライン */}
      <div
        className="pointer-events-none absolute left-0 top-0 bottom-0 w-px opacity-40"
        style={{
          background:
            'linear-gradient(180deg, transparent 0%, rgba(168,205,217,0.5) 20%, rgba(168,205,217,0.5) 80%, transparent 100%)',
        }}
        aria-hidden={true}
      />

      <div className="relative z-10 w-full max-w-5xl mx-auto px-8 lg:px-16">
        {/* 英字ラベル */}
        <div className="fade-in-up mb-6">
          <span
            className="section-label-en font-montserrat font-light uppercase"
            style={{ color: '#A8CDD9' }}
          >
            OUR BUSINESS
          </span>
        </div>

        {/* 見出し：会社概要と同様に中央寄せ */}
        <SectionHeadingContainer>
          <h2
            className="section-heading fade-in-up delay-1 mb-8 text-center lg:mb-10"
            style={{
              color: '#1A1A1A',
            }}
          >
            事業内容
          </h2>
        </SectionHeadingContainer>

        {/* リード文 */}
        <p
          className="fade-in-up delay-2 mx-auto mb-14 max-w-2xl text-center text-sm font-light leading-[2] sm:mb-16 sm:text-base"
          style={{
            color: '#666',
            fontFamily: "'Noto Sans JP', sans-serif",
            fontWeight: 300,
          }}
        >
          営業支援を軸に、企業と個人の成長を両立する事業を展開しています。
        </p>

        {/* 3カラムグリッド：横並びの一覧から、番号＋アイコン＋本文のカード風ではないブロックへ */}
        <div className="grid grid-cols-1 gap-12 border-t border-solid pt-12 sm:gap-14 md:grid-cols-3 md:gap-10 md:border-t-0 md:pt-0 lg:gap-12">
          {businesses.map((biz, i) => (
            <article
              key={biz.title}
              className={`fade-in-up flex min-w-0 flex-col items-center border-b border-solid pb-12 last:border-b-0 last:pb-0 md:items-start md:border-b-0 md:pb-0 md:text-left ${itemDelays[i] ?? 'delay-4'} business-item rounded-lg px-1 py-2 transition-colors md:px-0`}
              style={{
                borderColor: 'rgba(168, 205, 217, 0.35)',
              }}
            >
              <span
                className="font-montserrat text-3xl font-light tabular-nums leading-none sm:text-4xl"
                style={{ color: '#B89B4A', letterSpacing: '-0.02em' }}
                aria-hidden={true}
              >
                {String(i + 1).padStart(2, '0')}
              </span>

              <div
                className="mt-5 mb-5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full sm:h-12 sm:w-12"
                style={{
                  border: '1px solid rgba(184, 155, 74, 0.4)',
                  color: '#B89B4A',
                }}
              >
                <i className={`${biz.icon} text-xl sm:text-2xl`} aria-hidden={true} />
              </div>

              <h3
                className="mb-3 font-light"
                style={{
                  color: '#1A1A1A',
                  fontSize: 'clamp(15px, 1.6vw, 19px)',
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontWeight: 300,
                  letterSpacing: '0.06em',
                }}
              >
                {biz.title}
              </h3>
              <p
                className="max-w-sm font-light leading-[2] md:max-w-none"
                style={{
                  color: '#3A3A3A',
                  fontSize: 'clamp(13px, 1.15vw, 15px)',
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontWeight: 300,
                }}
              >
                {biz.desc}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
